<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { X, Send, AlertCircle } from 'lucide-vue-next'
import { useToast } from '@/core/composables/useToast'
import apiClient from '@/core/services/api'

const props = defineProps<{
  visible: boolean
  errorRef?: string | null
  errorMessage?: string
}>()

const emit = defineEmits<{
  close: []
}>()

const router = useRouter()
const { success: toastSuccess, error: toastError } = useToast()

type TicketCategory = 'bug' | 'feature' | 'question' | 'billing'
type TicketPriority = 'low' | 'medium' | 'high' | 'critical'

const submitting = ref(false)
const form = ref({
  subject: '',
  description: '',
  category: 'bug' as TicketCategory,
  priority: 'medium' as TicketPriority,
  error_ref: '' as string,
  page_url: '',
  browser_info: ''
})

const categories: Array<{ value: TicketCategory; label: string }> = [
  { value: 'bug', label: 'Bug / Errore' },
  { value: 'feature', label: 'Nuova Funzionalità' },
  { value: 'question', label: 'Domanda' },
  { value: 'billing', label: 'Fatturazione' },
]

const priorities: Array<{ value: TicketPriority; label: string }> = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Critica' },
]

watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    const hasError = props.errorRef || props.errorMessage
    form.value.subject = props.errorRef
      ? `Errore: ${props.errorRef}`
      : props.errorMessage
        ? `Errore: ${props.errorMessage.substring(0, 100)}`
        : ''
    form.value.description = props.errorMessage
      ? `Errore riscontrato:\n${props.errorMessage}\n\nPagina: ${router.currentRoute.value.fullPath}\n\nDescrivi cosa stavi facendo:\n`
      : ''
    form.value.category = hasError ? 'bug' : 'bug'
    form.value.priority = 'medium'
    form.value.error_ref = props.errorRef || ''
    form.value.page_url = router.currentRoute.value.fullPath
    form.value.browser_info = navigator.userAgent.substring(0, 500)
  }
})

async function submitTicket() {
  if (!form.value.subject.trim() || !form.value.description.trim()) {
    toastError('Compila tutti i campi obbligatori')
    return
  }

  submitting.value = true
  try {
    await apiClient.post('/api/support/tickets', {
      subject: form.value.subject.trim(),
      description: form.value.description.trim(),
      category: form.value.category,
      priority: form.value.priority,
      error_ref: form.value.error_ref || null,
      page_url: form.value.page_url,
      browser_info: form.value.browser_info,
    })
    toastSuccess('Ticket inviato con successo! Ti contatteremo al più presto.')
    emit('close')
  } catch (err: unknown) {
    if (err instanceof Error) {
      toastError(`Errore nell'invio del ticket: ${err.message}`)
    } else {
      toastError('Errore nell\'invio del ticket')
    }
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="visible"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        @click.self="$emit('close')"
      >
        <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <!-- Header -->
          <div class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
            <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <AlertCircle class="w-5 h-5 text-primary-600" />
              Segnala un Problema
            </h2>
            <button
              @click="$emit('close')"
              class="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              aria-label="Chiudi"
            >
              <X class="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <!-- Form -->
          <form @submit.prevent="submitTicket" class="p-4 space-y-4">
            <!-- Subject -->
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Oggetto <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.subject"
                type="text"
                maxlength="200"
                required
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Descrivi brevemente il problema..."
              />
            </div>

            <!-- Category + Priority -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Categoria
                </label>
                <select
                  v-model="form.category"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option v-for="cat in categories" :key="cat.value" :value="cat.value">
                    {{ cat.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Priorità
                </label>
                <select
                  v-model="form.priority"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option v-for="p in priorities" :key="p.value" :value="p.value">
                    {{ p.label }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Descrizione <span class="text-red-500">*</span>
              </label>
              <textarea
                v-model="form.description"
                rows="5"
                required
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="Descrivi il problema in dettaglio. Cosa stavi facendo quando è successo?"
              />
            </div>

            <!-- Error Ref (read-only if pre-filled) -->
            <div v-if="form.error_ref" class="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
              <p class="text-xs text-slate-500 dark:text-slate-400 mb-1">Codice errore collegato:</p>
              <code class="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {{ form.error_ref }}
              </code>
            </div>

            <!-- Submit -->
            <div class="flex justify-end gap-2 pt-2">
              <button
                type="button"
                @click="$emit('close')"
                class="px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                :disabled="submitting"
                class="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send class="w-4 h-4" />
                {{ submitting ? 'Invio...' : 'Invia Segnalazione' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
